import { axios } from '@vyce/core/src/api';

import {
  CreateWorkerInvitesRequest,
  CreateWorkerInvitesResponse,
  GetCompanyWorkersResponse,
  GetInvitesRequest,
  InviteLocationsSearchRequest,
  InvitePaySchedulesSearchRequest,
  InviteTeamsSearchRequest,
  ListCompanyWorkersRequest,
  PaginatedList,
  RedeemActiveInviteCompany,
  RetryInviteItemResponse,
  SendStaffInviteRequest,
  StatusMessage,
  TeamPublicLinkRequest,
} from '../types';
import { ActiveEmployeeInvite, InviteListItem, PaySchedule, Site, Starters, Team } from '../../types';
import config from '../config';

const URL = `${config.gatewayApiUrl}`;

// Item filters (resource_type … reasons) ride along with the rest of the body
export const getInvitesRequest = ({ companyId, ...body }: GetInvitesRequest) => {
  return axios.post<PaginatedList<InviteListItem>>(
    `${URL}/public/v1/invites/companies/${companyId}/search`,
    body
  );
};

// 202 — processing has started; equal workflow_ids on a re-sent identical body mean
// the same run is still going, nobody is invited twice
export const createWorkerInvitesRequest = ({ companyId, payload }: CreateWorkerInvitesRequest) => {
  return axios.post<CreateWorkerInvitesResponse>(
    `${URL}/public/v1/invites/companies/${companyId}/workers`,
    payload
  );
};

// Invite-scoped searches: every worker / resource of the company, not narrowed by the caller's
// rights, so anyone can be invited anywhere. The module screens keep their permission-aware routes.
export const searchInviteWorkersRequest = (companyId: string, payload: ListCompanyWorkersRequest) => {
  return axios.post<GetCompanyWorkersResponse>(
    `${URL}/public/v1/invites/companies/${companyId}/workers/search`,
    payload
  );
};

export const searchInviteTeamsRequest = (companyId: string, payload: InviteTeamsSearchRequest) => {
  return axios.post<PaginatedList<Team>>(
    `${URL}/public/v1/invites/companies/${companyId}/teams/search`,
    payload
  );
};

export const searchInviteLocationsRequest = (companyId: string, payload: InviteLocationsSearchRequest) => {
  return axios.post<PaginatedList<Site>>(
    `${URL}/public/v1/invites/companies/${companyId}/locations/search`,
    payload
  );
};

export const searchInvitePaySchedulesRequest = (
  companyId: string,
  payload: InvitePaySchedulesSearchRequest
) => {
  return axios.post<PaginatedList<PaySchedule>>(
    `${URL}/public/v1/invites/companies/${companyId}/pay_schedules/search`,
    payload
  );
};

export const resendInviteRequest = (companyId: string, inviteId: string) => {
  return axios.post<StatusMessage>(
    `${URL}/public/v1/invites/companies/${companyId}/invites/${inviteId}/resend`
  );
};

// 202 — the line goes to pending at once and settles (accepted / failed) shortly after.
// 409 when it is no longer retryable (someone else retried it, or it already worked).
export const retryInviteItemRequest = (companyId: string, inviteId: string, itemId: string) => {
  return axios.post<RetryInviteItemResponse>(
    `${URL}/public/v1/invites/companies/${companyId}/invites/${inviteId}/items/${itemId}/retry`
  );
};

export const deleteInviteRequest = (companyId: string, inviteId: string) => {
  return axios.delete<StatusMessage>(`${URL}/public/v1/invites/companies/${companyId}/invites/${inviteId}`);
};

export const createStaffInviteRequest = (companyId: string, payload: SendStaffInviteRequest) => {
  return axios.post<StatusMessage>(`${URL}/public/v1/invites/companies/${companyId}/staff`, payload);
};

export const redeemActiveAdminInvitesRequest = () => {
  return axios.post<RedeemActiveInviteCompany[]>(`${URL}/public/v1/invites/staff/redeem/active`, {});
};

export const getUserInviteRequest = (inviteToken: string) => {
  return axios.get(`${URL}/public/v1/invites/tokens/${inviteToken}`);
};

export const getSentInvitesRequest = (params?: { limit?: number; offset?: number }) => {
  return axios.post<PaginatedList<ActiveEmployeeInvite>>(
    `${URL}/public/v1/invites/employees/search/active`,
    {
      limit: params?.limit ?? 50,
      offset: params?.offset ?? 0,
    }
  );
};
// Core api

export const redeemPublicInviteTokenRequest = (token: string, inviteToken: string, starters: Starters) => {
  return axios.post(`/invites/public_links/redeem_token/${inviteToken}`, {
    pay_starter_location: starters.location,
    pay_starter_manager: starters.manager,
  });
};

export const redeemPublicInviteToTeamTokenRequest = (inviteToken: string) => {
  return axios.post(`/invites/public_links/teams/redeem_token/${inviteToken}`);
};

export const createTeamPublicLinkRequest = ({ companyId, teamId }: TeamPublicLinkRequest) => {
  return axios.put(`/invites/companies/${companyId}/teams/${teamId}/public_links`);
};

export const getTeamPublicLinksRequest = ({ companyId, teamId }: TeamPublicLinkRequest) => {
  return axios.get(`/invites/companies/${companyId}/teams/${teamId}/public_links`);
};
